import React, { useState } from 'react';
import { Typography, Icon, Spinner } from '@scale/scaleui';
import CopyToClipboard from 'react-copy-to-clipboard';
import { makeStyles } from 'react-scale/components/design-system';
import { getTimeWorkedText, getCurrentClaimTimeForReviewLevel } from './utils';
import ChatBulkAuditDetails from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditDetails';
import { ChatBulkAuditBatch, SubtaskAssignment } from '../SpeedAudit/types';
import { faStairs } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useGetProjectClaimTimeMax } from 'react-scale/components/audits/ChatBulkAudit/apiClient';

interface ChatBulkAuditTaskInfoBlockProps {
  auditBatch: ChatBulkAuditBatch;
  attemptAudit: SubtaskAssignment;
}

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    height: '100%',
    gap: '12px',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
  },
  rightAlignRow: {
    marginLeft: 'auto',
    justifyContent: 'flex-end',
  },
  linkOverride: {
    color: '#6052FF !important',
    textDecoration: 'underline !important',
  },
}));

function ChatBulkAuditTaskInfoBlock(props: ChatBulkAuditTaskInfoBlockProps) {
  const { attemptAudit, auditBatch } = props;
  const auditBatchId = auditBatch._id;

  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const { data, error, isLoading } = useGetProjectClaimTimeMax(auditBatchId);
  const currentCapForReviewLevel = getCurrentClaimTimeForReviewLevel(
    data,
    attemptAudit.auditedAttempt?.attemptedAtReviewLevel,
  );
  const classes = useStyles();
  const attempt = attemptAudit.auditedAttempt;

  return (
    <div>
      {attempt && (
        <div className={classes.container}>
          <div className={classes.row}>
            <Typography.Subtitle2>Attempt {attempt._id.slice(-5)}</Typography.Subtitle2>
            <CopyToClipboard text={attempt._id}>
              <Icon icon={['far', 'copy']} size="sm" />
            </CopyToClipboard>
            <div className={classes.rightAlignRow}>
              <a className={classes.linkOverride} onClick={() => setOpenDetails(true)}>
                view details
              </a>
            </div>
          </div>
          <div className={classes.row}>
            {/* Workaround for this issue here: https://github.com/FortAwesome/react-fontawesome/issues/366 */}
            <Icon icon={faStairs as IconProp} size="sm" />
            <Typography.Paragraph2>
              Review level: L{attempt.attemptedAtReviewLevel}
            </Typography.Paragraph2>
          </div>
          <div className={classes.row}>
            <Icon icon={['far', 'clock']} size="sm" />
            <Typography.Paragraph2>
              Time: {getTimeWorkedText(attempt.timeSpentSecs)} (
              {isLoading ? (
                <Spinner size="sm" />
              ) : error ? (
                'Error'
              ) : (
                getTimeWorkedText(currentCapForReviewLevel)
              )}{' '}
              cap)
            </Typography.Paragraph2>
          </div>
          <ChatBulkAuditDetails
            open={openDetails}
            onClose={() => setOpenDetails(false)}
            auditBatch={auditBatch}
            attempt={attempt}
            contributorId={attempt.attemptedBy}
            taskInfo={attemptAudit.responses.map(r => ({
              attemptId: r.id,
              reviewLevel: r.attemptedAtReviewLevel,
              reviewerRatingScore: r.reviewerRatingScore,
              reviewStatus: r.reviewStatus,
              reviewOutcome: r.reviewOutcome,
            }))}
            detailType="Task"
          />
        </div>
      )}
    </div>
  );
}

export default React.memo(ChatBulkAuditTaskInfoBlock);
